import Typography from '@mui/material/Typography';
import * as React from 'react';
import { useState } from 'react';
import Box from '@mui/material/Box';
import * as amplitude from '@amplitude/analytics-browser';
import { Alert, IconButton } from '@mui/material';
import ShareIcon from '@mui/icons-material/Share';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import HelpIcon from '@mui/icons-material/Help';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import { ExternallyControlledDialog } from './dialogs';
import shareSummary from '../share';
import UsingAIWarning from './UsingAIWarning';
import InformedPendingHandler from './InformedPendingHandler';
import DefaultSummary from './DefaultSummary';
import useSummary from '../useSummary';

function RelatedStatementInfoDialog({ relatedStatement, originalStatement, sx, onRequestExact }) {
    const [dialogOpen, setDialogOpen] = useState(false);

    const onClickRequestExact = () => {
        setDialogOpen(false);
        onRequestExact();
    };
    return (
        <>
            <ExternallyControlledDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                title="Waarom doen we dit?">
                <Typography>
                    Helaas is het erg kostbaar om AI samenvattingen te maken. Zowel qua kosten als
                    vanuit een duurzaamheids-perspectief. Daarom hergebruiken we als het mogelijk is
                    eerder berekende resultaten. Wil je het exacte resultaat berekenen voor jouw
                    stelling: <b>{originalStatement}</b>? Gebruik dan de knop hieronder.
                </Typography>
                <Button sx={{ mt: '15px' }} variant="contained" onClick={onClickRequestExact}>
                    Bereken de exacte samenvatting
                </Button>
                <Alert color="primary" icon={<NewspaperIcon />} sx={{ mt: 2 }}>
                    Gebruik jij OpenVerkiezingen.nl voor journalistieke doeleinden? Dan helpen wij
                    jou graag met een versie waarin jij altijd direct antwoord krijgt op jouw
                    originele vraag zonder door dit venster te moeten klikken. Stuur een email naar{' '}
                    <Link color="secondary" href="mailto:info@openverkiezingen.nl">
                        info@openverkiezingen.nl
                    </Link>{' '}
                    om onbeperkt toegang te krijgen.
                </Alert>
            </ExternallyControlledDialog>
            <Alert
                color="primary"
                sx={{ ...{ mb: 1 }, ...sx }}
                icon={<InfoIcon />}
                action={
                    <IconButton color="primary" onClick={() => setDialogOpen(true)}>
                        <HelpIcon />
                    </IconButton>
                }>
                We tonen je het resultaat voor een vergelijkbare stelling: <b>{relatedStatement}</b>
                . Klik op het vraagteken hiernaast voor meer informatie.
            </Alert>
        </>
    );
}

function ShareButton({ generating, summary, query, party }) {
    const button = (
        <IconButton
            color="primary"
            sx={{ position: 'absolute', bottom: '0px', right: '0px' }}
            onClick={() => {
                shareSummary(query, summary, party);
                amplitude.track('Share summary');
            }}>
            <ShareIcon />
        </IconButton>
    );

    return !!navigator.share && !generating && summary && button;
}

export default function AISummary({ party, query, onClose, defaultElection }) {
    const [election, setElection] = useState(defaultElection);
    const [exact, setExact] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const { summary, generating, data, areFactsGrounded, errorMsg, pending } = useSummary(
        election,
        query,
        party,
        exact
    );

    const handleExpansion = panel => {
        if (!(panel === expanded)) {
            amplitude.track('Source panel clicked.', { panelID: panel });
        }
        setExpanded(panel === expanded ? false : panel);
    };

    return (
        <Box>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100px',
                    marginBottom: '15px',
                    position: 'relative'
                }}>
                <img
                    src={party?.logo}
                    style={{ maxWidth: '100px', maxHeight: '100px' }}
                    alt={`${party?.name} logo`}
                />
                <IconButton
                    sx={{ position: 'absolute', top: '0px', right: '0px' }}
                    onClick={onClose}>
                    <CloseIcon />
                </IconButton>
                <ShareButton
                    generating={generating}
                    summary={summary}
                    party={party}
                    query={query}
                />
            </Box>

            <Box sx={{ backgroundColor: t => t.palette.background.grey, p: 3 }}>
                <InformedPendingHandler
                    pending={pending}
                    party={party}
                    paragraphs={data?.paragraphs}
                />
                {(summary || errorMsg) && (
                    <>
                        {errorMsg && <Alert severity="error">{errorMsg}</Alert>}
                        {data?.approximate && (
                            <RelatedStatementInfoDialog
                                relatedStatement={data?.statement.statement}
                                originalStatement={query}
                                onRequestExact={() => setExact(true)}
                            />
                        )}
                        <DefaultSummary
                            sx={{
                                mt: 3,
                                mb: 1
                            }}
                            data={data}
                            summary={summary}
                            generating={generating}
                            handleExpansion={handleExpansion}
                            expanded={expanded}
                            party={party}
                            election={election}
                            setElection={setElection}
                        />
                        {!pending && (
                            <UsingAIWarning
                                areFactsGrounded={areFactsGrounded}
                                generating={generating}
                                summary={summary}
                                party={party}
                                paragraphs={data?.paragraphs}
                                sx={{ mt: 3 }}
                            />
                        )}
                    </>
                )}
            </Box>
        </Box>
    );
}
